'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import scrollToTopOfProductGrid from 'components/consumer/ProductCardGridUI/helpers/scrollToTopOfProductGrid';
import { NO_RESULTS_DELAY_MS } from 'commons/constants';
import { isSortOnlyFilterStateChange } from '../components/AdvancedSearchProductHits/utils';
import { replaceSearchFilterParams } from '../searchPageUrlState';

/**
 * Manages filter transition state for the Algolia search results component.
 * Tracks when filters change, resets pagination, and controls skeleton/no-results display.
 *
 * Accepts refs (isPopRef, scrollRestoredRef, targetPagesRef) by reference — they are
 * intentionally excluded from the effect deps array because we always want their
 * current value at fire time, not to re-run the effect when they change.
 */
const useFilterTransitionState = t0 => {
  const $ = _c(31);
  const {
    filterStateKey,
    setIndexUiState,
    items,
    status,
    currentProductIndex,
    resultsIndex,
    activeTypeFilter,
    pagesUrlParam,
    isPopRef,
    scrollRestoredRef,
    targetPagesRef
  } = t0;
  const isFirstMountRef = useRef(true);
  const previousFilterStateKeyRef = useRef(null);
  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);
  const [isFilterTransitioning, setIsFilterTransitioning] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);
  let t1;
  let t2;
  if ($[0] !== activeTypeFilter || $[1] !== filterStateKey || $[2] !== isPopRef || $[3] !== pagesUrlParam || $[4] !== scrollRestoredRef || $[5] !== setIndexUiState || $[6] !== targetPagesRef) {
    t1 = () => {
      if (isFirstMountRef.current) {
        isFirstMountRef.current = false;
        previousFilterStateKeyRef.current = filterStateKey;
        targetPagesRef.current = pagesUrlParam;
        if (!(isPopRef.current && pagesUrlParam > 0)) {
          setIndexUiState(_temp);
        }
        return;
      }
      if (filterStateKey !== previousFilterStateKeyRef.current) {
        const onlySortChanged = isSortOnlyFilterStateChange(previousFilterStateKeyRef.current, filterStateKey);
        previousFilterStateKeyRef.current = filterStateKey;
        if (onlySortChanged) {
          targetPagesRef.current = 0;
          setIsFilterTransitioning(true);
          scrollToTopOfProductGrid(false);
          return;
        }
        if (isPopRef.current && !scrollRestoredRef.current) {
          if (activeTypeFilter) {
            setIndexUiState(prev_0 => {
              if (prev_0?.refinementList?.type?.[0] === activeTypeFilter) {
                return prev_0;
              }
              return {
                ...prev_0,
                refinementList: {
                  ...(prev_0.refinementList || {}),
                  type: [activeTypeFilter]
                }
              };
            });
          }
          return;
        }
        targetPagesRef.current = 0;
        setIsFilterTransitioning(true);
        setIndexUiState(prev_1 => {
          const next = {
            ...prev_1,
            page: 0
          };
          if (activeTypeFilter && !next.refinementList?.type?.length) {
            next.refinementList = {
              ...(next.refinementList || {}),
              type: [activeTypeFilter]
            };
          }
          return next;
        });
        replaceSearchFilterParams({
          pages: undefined
        });
        scrollToTopOfProductGrid(false);
      }
    };
    t2 = [filterStateKey, pagesUrlParam, setIndexUiState, activeTypeFilter, isPopRef, scrollRestoredRef, targetPagesRef];
    $[0] = activeTypeFilter;
    $[1] = filterStateKey;
    $[2] = isPopRef;
    $[3] = pagesUrlParam;
    $[4] = scrollRestoredRef;
    $[5] = setIndexUiState;
    $[6] = targetPagesRef;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[9] !== currentProductIndex || $[10] !== isFilterTransitioning || $[11] !== items || $[12] !== resultsIndex || $[13] !== status) {
    t3 = () => {
      if (!isFilterTransitioning) {
        return;
      }
      if (currentProductIndex && resultsIndex && resultsIndex !== currentProductIndex) {
        return;
      }
      if (items?.length > 0) {
        setIsFilterTransitioning(false);
      } else {
        if (status === "idle" && items !== undefined) {
          setIsFilterTransitioning(false);
        }
      }
    };
    t4 = [isFilterTransitioning, items, status, currentProductIndex, resultsIndex];
    $[9] = currentProductIndex;
    $[10] = isFilterTransitioning;
    $[11] = items;
    $[12] = resultsIndex;
    $[13] = status;
    $[14] = t3;
    $[15] = t4;
  } else {
    t3 = $[14];
    t4 = $[15];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[16] !== hasSearchCompleted || $[17] !== items || $[18] !== status) {
    t5 = () => {
      if (status === "idle" && !hasSearchCompleted && items !== undefined) {
        setHasSearchCompleted(true);
      }
    };
    t6 = [status, hasSearchCompleted, items];
    $[16] = hasSearchCompleted;
    $[17] = items;
    $[18] = status;
    $[19] = t5;
    $[20] = t6;
  } else {
    t5 = $[19];
    t6 = $[20];
  }
  useEffect(t5, t6);
  const isSearchPending = status === "loading" || status === "stalled";
  let t7;
  if ($[21] !== isSearchPending || $[22] !== items?.length) {
    t7 = () => {
      if (items?.length > 0 || isSearchPending) {
        setShowNoResults(false);
        return;
      }
      const timer = setTimeout(() => {
        setShowNoResults(true);
      }, NO_RESULTS_DELAY_MS);
      return () => clearTimeout(timer);
    };
    $[21] = isSearchPending;
    $[22] = items?.length;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = items?.length;
  let t9;
  if ($[24] !== isSearchPending || $[25] !== t8) {
    t9 = [t8, isSearchPending];
    $[24] = isSearchPending;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  useEffect(t7, t9);
  let t10;
  if ($[27] !== hasSearchCompleted || $[28] !== isFilterTransitioning || $[29] !== showNoResults) {
    t10 = {
      isFilterTransitioning,
      hasSearchCompleted,
      showNoResults
    };
    $[27] = hasSearchCompleted;
    $[28] = isFilterTransitioning;
    $[29] = showNoResults;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};
export default useFilterTransitionState;
function _temp(prev) {
  return prev?.page === 0 ? prev : {
    ...prev,
    page: 0
  };
}